import { useQueryClient } from '@tanstack/react-query'
import { Moment } from 'moment'
import React from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { ReceiptReminderSuccessToastMessage } from 'src/app/Toast/Toast'
import {
  ORG_EXPENSE_REPORTS_QK,
  ORG_QK,
  RECEIPT_REMINDER_STATUS,
  receiptReminderQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { createSetIsSendReceiptReminderPopupOpen } from 'src/redux/actions/app.actions'

export interface GetOrgReceiptReminderStatusResponse {
  usersMissingReceiptsCount: number
  lastReceiptReminderSentAt: Moment
}

interface QueryOrgReceiptReminderStatusOptions
  extends ExtendBaseQueryOptions<GetOrgReceiptReminderStatusResponse> {
  orgId: string | undefined
}

export const useQueryOrgReceiptReminderStatus = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrgReceiptReminderStatusOptions) => {
  return useExtendQuery<GetOrgReceiptReminderStatusResponse>({
    queryKey: receiptReminderQueryKeys.orgReceiptReminders(orgId),
    queryUrl: () => `organizations/${orgId}/receiptreminderstatus`,
    enabled: enabled && orgId !== undefined,
    ...rest,
  })
}

interface QueryOrgExpReportReceiptReminderStatusOptions
  extends ExtendBaseQueryOptions<GetOrgReceiptReminderStatusResponse> {
  reportId: string | undefined
}

export const useQueryOrgExpReportReceiptReminderStatus = ({
  reportId,
  enabled = true,
  ...rest
}: QueryOrgExpReportReceiptReminderStatusOptions) => {
  return useExtendQuery<GetOrgReceiptReminderStatusResponse>({
    queryKey:
      receiptReminderQueryKeys.orgExpenseReportReceiptReminders(reportId),
    queryUrl: () => `orgexpensereports/${reportId}/receiptreminderstatus`,
    enabled: enabled && reportId !== undefined,
    ...rest,
  })
}

interface PostSendReceiptReminderVariables
  extends UseExtendMutationVariablesBody {
  id: string
}

export const usePostSendOrgReceiptReminder = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendMutation<
    PostSendReceiptReminderVariables,
    PostSendReceiptReminderVariables
  >(MutationMethod.POST, {
    mutationUrl: (variables) =>
      `/organizations/${variables.id}/receiptreminder`,
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORG_QK, variables.id, RECEIPT_REMINDER_STATUS],
      })
      dispatch(createSetIsSendReceiptReminderPopupOpen(false))
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <ReceiptReminderSuccessToastMessage />,
        }),
      )
    },
  })
}

export const usePostSendOrgExpReportReceiptReminder = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendMutation<
    PostSendReceiptReminderVariables,
    PostSendReceiptReminderVariables
  >(MutationMethod.POST, {
    mutationUrl: (variables) =>
      `/orgexpensereports/${variables.id}/receiptreminder`,
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ORG_EXPENSE_REPORTS_QK,
          variables.id,
          RECEIPT_REMINDER_STATUS,
        ],
      })
      dispatch(createSetIsSendReceiptReminderPopupOpen(false))
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <ReceiptReminderSuccessToastMessage />,
        }),
      )
    },
  })
}
